import type { Lang } from "./i18n";
import { localeRoot } from "./routes";

export type HomeLocale = Lang | "zh-HK";
export type HomeFeatureId = "always" | "channels" | "languages" | "systems" | "free";
export type ExpandableFeatureId = HomeFeatureId;
export const hongKongHomePath = "/hk/ai-receptionist";
export const unitedStatesHomePath = "/en/ai-receptionist";

export function homeBaseLang(locale: HomeLocale): Lang {
  return locale === "zh-HK" ? "zh" : locale;
}

export function homePath(locale: HomeLocale, search = "") {
  const localized = localeRoot(homeBaseLang(locale), search);
  const queryIndex = localized.indexOf("?");
  const query = queryIndex < 0 ? "" : localized.slice(queryIndex);
  if (locale === "zh-HK") return `${hongKongHomePath}${query}`;
  return localized;
}

export const homeLocales: Array<{ value: HomeLocale; label: string; htmlLang: string }> = [
  { value: "zh-HK", label: "繁體中文（香港）", htmlLang: "zh-HK" },
  { value: "zh", label: "简体中文", htmlLang: "zh-CN" },
  { value: "en", label: "English", htmlLang: "en" },
];

export interface HomeExample {
  id: string;
  label: string;
  detail?: string;
  title: string;
  description: string;
  request?: string;
  reply?: string;
  conversationLang?: string;
  steps?: string[];
}

export interface HomeFeature {
  id: HomeFeatureId;
  label: string;
  example: HomeExample;
  options?: HomeExample[];
}

const copy = (locale: HomeLocale, en: string, zh: string, hk: string) => locale === "en" ? en : locale === "zh" ? zh : hk;

export function getHomeContent(locale: HomeLocale) {
  const c = (en: string, zh: string, hk: string) => copy(locale, en, zh, hk);
  const languageExamples: HomeExample[] = [
    {
      id: "yue", label: c("Cantonese", "广东话", "廣東話"),
      title: c("A familiar voice, in Cantonese.", "用广东话，自然接待每位客户。", "客人講廣東話，ClawEase 自然接得住。"),
      description: c("Understand the request, collect the details, and continue the conversation in Cantonese.", "听懂客户需求，用广东话完成咨询和预约沟通。", "聽明客人需要，以廣東話處理查詢及預約溝通。"),
      request: "你好，我想約聽日下晝。", reply: "可以呀，你想約幾點？", conversationLang: "yue-Hant",
    },
    {
      id: "mandarin", label: c("Mandarin", "普通话", "普通話"),
      title: c("Keep the conversation flowing in Mandarin.", "普通话沟通，需求清楚、跟进到位。", "普通話溝通，清楚理解每個需要。"),
      description: c("Answer questions and gather booking details in Mandarin.", "从回答问题到整理预约资料，让每一步沟通更顺畅。", "由回答問題到整理預約資料，讓每一步溝通更順暢。"),
      request: "你好，我想预约明天下午。", reply: "好的，请问您希望预约几点？", conversationLang: "zh-CN",
    },
    {
      id: "english", label: c("English", "英语", "英語"),
      title: c("Welcome your customers in English.", "英语接待，让沟通更轻松。", "英語接待，輕鬆照顧不同客人。"),
      description: c("Answer questions and follow up on requests in your customer's language.", "用客户熟悉的语言回答问题并跟进需求。", "用客人熟悉的語言回答問題，繼續跟進需要。"),
      request: "Hi, can I book an appointment for tomorrow afternoon?", reply: "Of course. What time works best for you?", conversationLang: "en",
    },
    {
      id: "more-languages", label: c("More languages", "更多语言", "更多語言"),
      title: c("One receptionist. More languages.", "一个接待员，支持更多语言。", "一位接待員，照顧更多語言需要。"),
      description: c("Choose the language coverage that fits your customers. Available languages depend on your plan.", "按客户需要选择适合的语言覆盖，具体支持范围以套餐为准。", "按客人需要選擇合適的語言支援，具體範圍以方案為準。"),
      steps: [c("Understand the language", "识别沟通语言", "識別溝通語言"), c("Understand the request", "理解客户需求", "理解客人需要"), c("Keep following up", "继续跟进", "繼續跟進")],
    },
  ];
  const channelExamples: HomeExample[] = [
    {
      id: "whatsapp", label: "WhatsApp",
      title: c("Turn a WhatsApp message into the next step.", "WhatsApp 消息，接得住也跟得进。", "WhatsApp 訊息，接得住、跟得進。"),
      description: c("Handle inquiries, collect booking details, and follow up in the same conversation.", "接收咨询、整理预约资料，在同一段对话里继续跟进。", "接收查詢、整理預約資料，在同一段對話繼續跟進。"),
      request: c("Are you available tomorrow?", "请问明天可以预约吗？", "請問聽日可以預約嗎？"),
      reply: c("Happy to help. Which service would you like to book?", "可以帮您安排，请问需要预约什么服务？", "可以幫你安排，請問想預約咩服務？"),
    },
    {
      id: "phone", label: c("Phone", "电话", "電話"),
      title: c("Every call is a chance to help.", "每通来电，都有回应。", "每通來電，都有人回應。"),
      description: c("Answer calls, understand the request, and collect the details needed for follow-up.", "接听来电、理解需求，整理后续处理所需的信息。", "接聽來電、理解需要，整理後續處理所需的資料。"),
      steps: [c("Answer the call", "接听来电", "接聽來電"), c("Collect the details", "整理关键信息", "整理關鍵資料"), c("Arrange follow-up", "安排后续跟进", "安排後續跟進")],
    },
    {
      id: "email", label: c("Email", "邮件", "電郵"),
      title: c("Give every email a clear next step.", "邮件咨询，有序跟进。", "電郵查詢，有序跟進。"),
      description: c("Read the request, collect missing information, and keep the reply in context.", "理解邮件内容、补齐所需资料，让回复和跟进保持连贯。", "理解電郵內容、補齊所需資料，讓回覆和跟進保持連貫。"),
      request: c("Could you send me more details about your services?", "可以介绍一下你们的服务吗？", "可以介紹一下你哋嘅服務嗎？"),
      reply: c("Certainly. Which service are you interested in?", "当然可以，请问您想了解哪项服务？", "當然可以，請問你想了解邊項服務？"),
    },
    {
      id: "more-channels", label: c("More channels", "更多渠道", "更多渠道"),
      title: c("Bring more customer conversations together.", "更多沟通渠道，统一跟进。", "更多溝通渠道，統一跟進。"),
      description: c("Support requests from web chat, forms, and other available channels in one workflow.", "将网页聊天、表单及其他支持渠道的需求接入同一套处理流程。", "將網頁聊天、表單及其他支援渠道的需要接入同一套處理流程。"),
      steps: [c("Web chat", "网页聊天", "網頁聊天"), c("Web forms", "网页表单", "網頁表單"), c("More touchpoints", "更多客户触点", "更多客人接觸點")],
    },
  ];
  const systemExamples: HomeExample[] = [
    {
      id: "calendar", label: "Calendar",
      title: c("Let the conversation lead to your calendar.", "从客户对话，接到日程安排。", "由客人對話，接到日程安排。"),
      description: c("Connect your calendar to coordinate availability, appointments, and confirmations.", "接通日历，协调可预约时间、日程安排与预约确认。", "接通日曆，協調可預約時間、日程安排及預約確認。"),
      steps: [c("Check availability", "查询可用时间", "查詢可用時間"), c("Arrange the booking", "安排预约", "安排預約"), c("Send confirmation", "发送确认", "發送確認")],
    },
    {
      id: "pms", label: "PMS",
      title: c("Connect reception with your PMS.", "让接待与 PMS 业务系统相连。", "讓接待與 PMS 業務系統相連。"),
      description: c("Bring customer details and appointment requests into your existing practice or property workflow.", "将客户资料和预约需求衔接到现有门店或物业业务流程。", "將客人資料和預約需要銜接到現有門店或物業業務流程。"),
      steps: [c("Collect details", "收集客户资料", "收集客人資料"), c("Find the record", "查找业务记录", "查找業務記錄"), c("Update the system", "同步业务系统", "同步業務系統")],
    },
    {
      id: "crm", label: "CRM",
      title: c("Keep your customer records in the loop.", "每次沟通，都能衔接客户记录。", "每次溝通，都能銜接客人記錄。"),
      description: c("Organize contact details, inquiry context, and the next follow-up in your CRM workflow.", "整理联系方式、咨询内容和后续跟进，衔接现有 CRM 流程。", "整理聯絡資料、查詢內容及後續跟進，銜接現有 CRM 流程。"),
      steps: [c("Identify the customer", "识别客户", "識別客人"), c("Record the request", "记录需求", "記錄需要"), c("Arrange follow-up", "安排跟进", "安排跟進")],
    },
    {
      id: "more-systems", label: c("More systems", "更多系统", "更多系統"),
      title: c("Work with the systems you already use.", "衔接你已经在用的系统。", "銜接你已經在用的系統。"),
      description: c("Connect supported business tools and browser workflows to complete the next step. Setup depends on your system.", "接入支持的业务工具和网页工作流，具体配置根据所用系统确认。", "接入支援的業務工具及網頁工作流程，具體設定按所用系統確認。"),
      steps: [c("Understand your workflow", "了解业务流程", "了解業務流程"), c("Connect your tools", "接入现有工具", "接入現有工具"), c("Complete the next step", "完成后续操作", "完成後續操作")],
    },
  ];
  const always: HomeExample = {
    id: "always", label: c("Always on", "全天候接待", "全天候接待"),
    title: c("Ready when your customers are.", "客户来找你，随时有人接。", "客人搵你，隨時有人接。"),
    description: c("Keep handling calls and messages after hours, on weekends, and while your team is busy.", "下班后、周末或团队忙碌时，继续接收来电和消息、整理客户需求。", "收工後、週末或團隊忙碌時，繼續接收來電和訊息、整理客人需要。"),
    steps: [c("After hours", "下班之后", "收工之後"), c("On weekends", "周末时段", "週末時段"), c("During busy periods", "业务高峰", "業務繁忙時段")],
  };
  const free: HomeExample = {
    id: "free", label: c("Free forever", "永久免费", "永久免費"),
    title: c("Start with a plan that stays free.", "从永久免费方案开始。", "由永久免費方案開始。"),
    description: c("The Free plan has no subscription fee. Calls and additional usage follow the published pricing; upgrade when you need more.", "Free 方案无需订阅费，通话及额外用量按现有定价计费，需要更多能力时再升级。", "Free 方案無需訂閱費，通話及額外用量按現有收費計算，需要更多功能時再升級。"),
    steps: [c("No subscription fee", "无需订阅费", "無需訂閱費"), c("Start with Free", "从 Free 开始", "由 Free 開始"), c("Upgrade as you grow", "按需升级", "按需要升級")],
  };
  const alwaysOptions: HomeExample[] = [
    {
      id: "always-night", label: c("After hours", "夜间接待", "夜間接待"),
      title: c("Keep answering after closing time.", "下班之后，继续接待。", "收工之後，繼續接待。"),
      description: c("Handle calls and messages while your team is off duty.", "团队下班后，继续接收来电和消息。", "團隊收工後，繼續接收來電和訊息。"),
    },
    {
      id: "always-weekend", label: c("Weekends too", "周末无休", "週末無休"),
      title: c("Stay available through the weekend.", "周末也有回应。", "週末亦有回應。"),
      description: c("Collect customer inquiries and booking requests on weekends.", "周末照常接收咨询与预约需求。", "週末照常接收查詢與預約需要。"),
    },
    {
      id: "always-busy", label: c("Efficient response", "高效响应", "高效回應"),
      title: c("Every inquiry answered right away.", "每个咨询都高效回应。", "每個查詢都高效回應。"),
      description: c("Handle multiple inquiries at once with quick, steady replies — customers never wait.", "同时处理多个咨询，回复快捷稳定，客人无需等待。", "同時處理多個查詢，回覆快捷穩定，客人無需等候。"),
    },
  ];
  const pricingOptions: HomeExample[] = [
    { ...free, id: "plan-free", label: "Free", detail: c("Free forever", "永久免费", "永久免費") },
    {
      id: "plan-pro", label: "US$39", detail: c("Pro · /mo", "Pro · /月", "Pro · /月"),
      title: c("Pro — US$39 per month.", "Pro — 每月 US$39。", "Pro — 每月 US$39。"),
      description: c("The Pro plan, billed monthly.", "Pro 方案，按月订阅。", "Pro 方案，按月訂閱。"),
    },
    {
      id: "plan-team", label: "US$79", detail: c("Team · /mo", "Team · /月", "Team · /月"),
      title: c("Team — US$79 per month.", "Team — 每月 US$79。", "Team — 每月 US$79。"),
      description: c("The Team plan, billed monthly.", "Team 方案，按月订阅。", "Team 方案，按月訂閱。"),
    },
  ];
  const features: HomeFeature[] = [
    { id: "always", label: c("24/7 reception", "24/7 全天候", "24/7 全天候"), example: always, options: alwaysOptions },
    { id: "channels", label: c("Multiple channels", "多种信息来源", "多種訊息來源"), example: channelExamples[0], options: channelExamples },
    { id: "languages", label: c("Multilingual", "多语言接待", "多語言接待"), example: languageExamples[0], options: languageExamples },
    { id: "systems", label: c("System integrations", "接入业务系统", "接入業務系統"), example: systemExamples[0], options: systemExamples },
    { id: "free", label: free.label, example: free, options: pricingOptions },
  ];
  return {
    headline: [c("A free AI receptionist,", "免费的 AI 前台，", "免費的 AI 前台，"), c("Answers, books, and follows up for you.", "帮你接待、预约与跟进。", "替你接待、預約與跟進。")],
    description: [c("Speak Cantonese, Mandarin, English, and more. Connect WhatsApp, phone, and email.", "支持广东话、普通话、英语等多种语言，整合 WhatsApp、电话及邮件。", "支援廣東話、普通話、英語等多種語言，整合 WhatsApp、電話及電郵。"), c("Bring Google Calendar, PMS, and CRM into the conversation.", "接通 Google Calendar、PMS、CRM，让每次接待都有跟进。", "接通 Google Calendar、PMS、CRM，讓每次接待都有跟進。")],
    nav: { features: c("Product", "产品功能", "產品功能"), how: c("How it works", "运作方式", "實際效益"), why: c("Why ClawEase", "选择理由", "選擇理由"), pricing: c("Pricing", "收费方案", "收費方案"), demo: c("Book a demo", "预约演示", "下載"), menu: c("Navigation", "导航菜单", "導覽選單"), language: c("Website language", "网站语言", "網站語言") },
    featureLabel: c("Explore ClawEase capabilities", "探索 ClawEase 功能", "探索 ClawEase 功能"),
    platformWords: [c("Answer", "接听", "接聽"), c("Understand", "理解", "理解"), c("Book", "预约", "預約"), c("Follow up", "跟进", "跟進")],
    platformLabel: c("ClawEase answers, understands, books and follows up — connecting Google Calendar, WhatsApp, Dentrix, Open Dental and Eaglesoft.", "ClawEase 平台:接听、理解、预约、跟进,连接 Google Calendar、WhatsApp、Dentrix、Open Dental、Eaglesoft。", "ClawEase 平台:接聽、理解、預約、跟進,連接 Google Calendar、WhatsApp、Dentrix、Open Dental、Eaglesoft。"),
    exampleLabel: c("Example workflow", "功能示例", "功能示例"),
    closeExample: c("Close example", "关闭示例", "關閉示例"),
    pricingLink: c("Explore plans", "查看收费方案", "查看收費方案"),
    features,
  };
}
